const AUTHORIZE_PATH = '/v1/oauth/authorize';

/**
 * Validates that a redirect URL is safe to navigate to after login.
 *
 * The redirect target must resolve to the current origin. This covers
 * both shapes auth-service may emit:
 *   - Relative paths like `/v1/oauth/authorize?...`
 *   - Absolute URLs on the current origin
 *
 * Resolution is performed with the same URL parser the browser uses
 * for `window.location.replace(url)`, which guarantees the validation
 * matches actual navigation behaviour. This neutralises bypasses that
 * a string-prefix check would miss — backslashes (treated as `/` in
 * http/https URLs), embedded tabs/newlines (stripped before parsing),
 * non-http schemes (origin is opaque), etc.
 *
 * Returns the URL if safe, or null otherwise.
 */
export const sanitizeRedirectParam = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }

  try {
    const resolved = new URL(url, window.location.origin);
    if (resolved.origin === window.location.origin) {
      return url;
    }
  } catch {
    return null;
  }

  return null;
};

/**
 * Like sanitizeRedirectParam, but only accepts the authorize endpoint. This is
 * the check on the post-reset redirect auth-service returns (it only bounds its
 * size); /oauth/authorize then validates the client and redirect_uri.
 */
export const sanitizeAuthorizeRedirect = (url: unknown): string | null => {
  const safe = typeof url === 'string' ? sanitizeRedirectParam(url) : null;
  return safe && new URL(safe, window.location.origin).pathname === AUTHORIZE_PATH ? safe : null;
};

/**
 * Validates that a logout redirect URL is safe to navigate to.
 * Only permits URLs that start with one of the allowed platform-app base
 * URLs — the `redirectUrl` query param on /logout always points to platform-app.
 *
 * Returns the URL if safe, or null otherwise.
 */
export const sanitizeLogoutRedirectUrl = (url: string | null | undefined, allowedBaseUrls: string[]): string | null => {
  if (!url) {
    return null;
  }

  const isAllowed = allowedBaseUrls.some((baseUrl) => baseUrl && url.startsWith(`${baseUrl}/`));

  return isAllowed ? url : null;
};
